/**
 * Date-only values — a trip's or a segment's first and last day.
 *
 * These are calendar days, not moments, but Firestore can only hold them as a
 * Timestamp. Until now the forms stored local midnight on whichever phone
 * saved them, so a day entered in Boston (04:00Z) read as the day before on a
 * phone in California, and a trip migrated at UTC midnight read a day early
 * everywhere in the Americas.
 *
 * New values are stored at **noon UTC**, which reads as the same date in every
 * zone from UTC-11 to UTC+11 — so even code that formats the Timestamp with a
 * plain `toLocaleDateString` gets it right. Reading goes through `dayKeyOf`,
 * which also recovers the intended day from every older shape: local midnight
 * anywhere from UTC-12 to UTC+11, UTC midnight, and noon UTC all land on the
 * day they were entered as.
 *
 * Days are handled as `"YYYY-MM-DD"` keys, which compare and sort as strings.
 */

const MS_PER_HOUR = 60 * 60 * 1000;
const MS_PER_DAY = 24 * MS_PER_HOUR;

type DateLike = { toDate?: () => Date } | Date | null | undefined;

function asDate(d: DateLike): Date | null {
  if (!d) return null;
  if (d instanceof Date) return d;
  return d.toDate?.() ?? null;
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function parseKey(key: string): [number, number, number] {
  const [y = 1970, m = 1, d = 1] = key.split("-").map(Number);
  return [y, m, d];
}

function utcKey(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/**
 * The calendar day a stored date-only value means.
 *
 * Every shape ever written falls in (UTC midnight − 12h, UTC midnight + 12h]
 * of its intended day, so shifting forward by just under 12h and reading the
 * UTC date recovers it.
 */
export function dayKeyOf(value: DateLike): string | null {
  const d = asDate(value);
  if (!d) return null;
  return utcKey(d.getTime() + 12 * MS_PER_HOUR - 1);
}

/** The Timestamp-able Date to store for a calendar day: noon UTC. */
export function dateFromDayKey(key: string): Date {
  const [y, m, d] = parseKey(key);
  return new Date(Date.UTC(y, m - 1, d, 12));
}

/** Today's calendar day where the device is right now. */
export function todayKey(now: Date = new Date()): string {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** The calendar day of an instant as read in a zone (device zone if none). */
export function dayKeyInZone(instant: Date, zone?: string | null): string {
  if (!zone) return todayKey(instant);
  try {
    // Assembled from parts rather than trusting a locale's layout to stay
    // YYYY-MM-DD across ICU versions.
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(instant);
    const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value;
    const y = get("year");
    const m = get("month");
    const d = get("day");
    return y && m && d ? `${y}-${m}-${d}` : todayKey(instant);
  } catch {
    return todayKey(instant);
  }
}

/** `key` moved by `days` calendar days. */
export function addDaysToKey(key: string, days: number): string {
  return utcKey(dateFromDayKey(key).getTime() + days * MS_PER_DAY);
}

/** Whole calendar days from `a` to `b` (b − a); DST can't skew it. */
export function daysBetweenKeys(a: string, b: string): number {
  return Math.round((dateFromDayKey(b).getTime() - dateFromDayKey(a).getTime()) / MS_PER_DAY);
}

/**
 * Midnight on the device's clock for a calendar day — for code that needs a
 * local Date to hand to date-picker style APIs or compare against local
 * midnights.
 */
export function localMidnightOfKey(key: string): Date {
  const [y, m, d] = parseKey(key);
  return new Date(y, m - 1, d);
}

/** Format a calendar day without letting any zone move it. */
export function formatDayKey(key: string, opts: Intl.DateTimeFormatOptions): string {
  return dateFromDayKey(key).toLocaleDateString(undefined, { ...opts, timeZone: "UTC" });
}

/** Whether `today` (default: the device's today) falls within [start, end]. */
export function dayRangeContains(
  start: DateLike,
  end: DateLike,
  today: string = todayKey(),
): boolean {
  const s = dayKeyOf(start);
  const e = dayKeyOf(end);
  if (!s || !e) return false;
  return today >= s && today <= e;
}
